import { useState } from 'react';
import type { ChangeEvent, FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Accomplishment,
  AccomplishmentHolder,
  DecorationCategory,
  DecorationKind,
} from '@clan-heart/shared';
import { DECORATION_CATEGORIES, decorationKindSchema } from '@clan-heart/shared';
import {
  awardAccomplishment,
  createAccomplishment,
  getAccomplishments,
  getMembers,
  updateAccomplishment,
  uploadFlag,
} from '../api.js';
import { Button } from '../components/Button.js';
import { useMe } from '../hooks/useMe.js';
import { awardCandidates } from './awardCandidates.js';
import styles from './ManagePage.module.css';

const DECORATION_KINDS = decorationKindSchema.options;
const DECORATION_KIND_LABELS: Record<DecorationKind, string> = {
  ribbon: 'Ribbon',
  medal: 'Medal',
  ensign: 'Ensign',
};

const MAX_ART_BYTES = 2 * 1024 * 1024;

/** The three star-threshold inputs as free-typed strings, bronze/silver/gold. */
type ThresholdInputs = [string, string, string];

function thresholdsToInputs(thresholds: readonly number[]): ThresholdInputs {
  return [thresholds[0]?.toString() ?? '', thresholds[1]?.toString() ?? '', thresholds[2]?.toString() ?? ''];
}

/** Parses the three threshold inputs into the array the API expects: [] when
 * all three are empty, else three rising whole numbers. Anything in between
 * throws, so the save shows a message instead of quietly dropping the stars. */
function inputsToThresholds(inputs: ThresholdInputs): number[] {
  if (inputs.every((v) => v.trim() === '')) return [];
  const nums = inputs.map((v) => Number(v.trim()));
  const valid =
    inputs.every((v) => v.trim() !== '') &&
    nums.every((n) => Number.isInteger(n) && n > 0) &&
    nums[0]! < nums[1]! &&
    nums[1]! < nums[2]!;
  if (!valid) {
    throw new Error('Fill in all three star thresholds as rising whole numbers (e.g. 3, 5, 10), or leave all three empty.');
  }
  return nums;
}

interface DecorationFieldsValue {
  decoration: DecorationKind;
  category: DecorationCategory;
  repeatable: boolean;
  thresholds: ThresholdInputs;
  art: string | null;
}

function DecorationFields({
  idPrefix,
  value,
  onChange,
}: {
  idPrefix: string;
  value: DecorationFieldsValue;
  onChange: (next: DecorationFieldsValue) => void;
}) {
  const { decoration, category, repeatable, thresholds, art } = value;

  return (
    <>
      <div className={styles.fieldRow}>
        <div className={styles.field}>
          <label htmlFor={`${idPrefix}-decoration`}>Decoration</label>
          <select
            id={`${idPrefix}-decoration`}
            value={decoration}
            onChange={(e) => onChange({ ...value, decoration: e.target.value as DecorationKind })}
          >
            {DECORATION_KINDS.map((kind) => (
              <option key={kind} value={kind}>
                {DECORATION_KIND_LABELS[kind]}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.field}>
          <label htmlFor={`${idPrefix}-category`}>Category</label>
          <select
            id={`${idPrefix}-category`}
            value={category}
            onChange={(e) => onChange({ ...value, category: e.target.value as DecorationCategory })}
          >
            {DECORATION_CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className={styles.checkboxField}>
        <label htmlFor={`${idPrefix}-repeatable`}>
          <input
            id={`${idPrefix}-repeatable`}
            type="checkbox"
            checked={repeatable}
            onChange={(e) =>
              onChange({
                ...value,
                repeatable: e.target.checked,
                thresholds: e.target.checked ? thresholds : ['', '', ''],
              })
            }
          />
          Repeatable
        </label>
      </div>

      {repeatable && (
        <div className={styles.field}>
          <span>Service stars (approved-claim counts, ascending)</span>
          <div className={styles.thresholdRow}>
            {(['Bronze', 'Silver', 'Gold'] as const).map((label, i) => (
              <input
                key={label}
                aria-label={`${label} star threshold`}
                type="number"
                min={1}
                placeholder={label}
                value={thresholds[i]}
                onChange={(e) => {
                  const next = [...thresholds] as ThresholdInputs;
                  next[i] = e.target.value;
                  onChange({ ...value, thresholds: next });
                }}
              />
            ))}
          </div>
        </div>
      )}

      <div className={styles.field}>
        <span>Art</span>
        <ArtUploader value={art} onChange={(next) => onChange({ ...value, art: next })} />
      </div>
    </>
  );
}

function ArtUploader({ value, onChange }: { value: string | null; onChange: (art: string | null) => void }) {
  const [error, setError] = useState<string | null>(null);

  const uploadMutation = useMutation({
    mutationFn: uploadFlag,
    onSuccess: (res) => {
      setError(null);
      onChange(res.flag);
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Upload failed.'),
  });

  function handleFileChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = ''; // lets the same file be picked again after an error
    if (!file) return;
    setError(null);
    if (file.size > MAX_ART_BYTES) {
      setError('Image must be 2 MB or smaller.');
      return;
    }
    uploadMutation.mutate(file);
  }

  return (
    <div className={styles.artUploader}>
      {value ? (
        <div className={styles.artPreview}>
          <img src={value} alt="" />
          <Button type="button" variant="ghost" onClick={() => onChange(null)}>
            Remove
          </Button>
        </div>
      ) : (
        <div className={styles.artPlaceholder} aria-hidden="true" />
      )}
      <label className={styles.uploadLabel}>
        {uploadMutation.isPending ? 'Uploading…' : value ? 'Replace image' : 'Upload image'}
        <input
          type="file"
          accept="image/png,image/webp,image/jpeg"
          className={styles.uploadInput}
          onChange={handleFileChange}
          disabled={uploadMutation.isPending}
        />
      </label>
      {error && <p className={styles.error}>{error}</p>}
    </div>
  );
}

function ManageRow({ accomplishment }: { accomplishment: Accomplishment }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [awarding, setAwarding] = useState(false);
  const [title, setTitle] = useState(accomplishment.title);
  const [description, setDescription] = useState(accomplishment.description);
  const [points, setPoints] = useState(String(accomplishment.points));
  const [decorationValue, setDecorationValue] = useState<DecorationFieldsValue>({
    decoration: accomplishment.decoration,
    category: accomplishment.category,
    repeatable: accomplishment.repeatable,
    thresholds: thresholdsToInputs(accomplishment.starThresholds),
    art: accomplishment.art,
  });
  const [error, setError] = useState<string | null>(null);

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ['accomplishments'] });
  }

  const saveMutation = useMutation({
    mutationFn: () =>
      updateAccomplishment(accomplishment.id, {
        title,
        description,
        points: Number(points),
        decoration: decorationValue.decoration,
        category: decorationValue.category,
        repeatable: decorationValue.repeatable,
        starThresholds: decorationValue.repeatable ? inputsToThresholds(decorationValue.thresholds) : [],
        art: decorationValue.art,
      }),
    onSuccess: () => {
      invalidate();
      setEditing(false);
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Something went wrong.'),
  });

  const archiveMutation = useMutation({
    mutationFn: (archived: boolean) => updateAccomplishment(accomplishment.id, { archived }),
    onSuccess: invalidate,
    onError: (err) => setError(err instanceof Error ? err.message : 'Something went wrong.'),
  });

  function handleSave(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    saveMutation.mutate();
  }

  const isArchived = accomplishment.archivedAt !== null;

  if (editing) {
    return (
      <article className={styles.row}>
        <form className={styles.createForm} onSubmit={handleSave}>
          <div className={styles.field}>
            <label htmlFor={`title-${accomplishment.id}`}>Title</label>
            <input
              id={`title-${accomplishment.id}`}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </div>
          <div className={styles.field}>
            <label htmlFor={`description-${accomplishment.id}`}>Description</label>
            <textarea
              id={`description-${accomplishment.id}`}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </div>
          <div className={styles.field}>
            <label htmlFor={`points-${accomplishment.id}`}>Points</label>
            <input
              id={`points-${accomplishment.id}`}
              type="number"
              min={0}
              value={points}
              onChange={(e) => setPoints(e.target.value)}
            />
          </div>
          <DecorationFields
            idPrefix={`edit-${accomplishment.id}`}
            value={decorationValue}
            onChange={setDecorationValue}
          />
          {error && <p className={styles.error}>{error}</p>}
          <div className={styles.actions}>
            <Button type="submit" variant="primary" disabled={saveMutation.isPending}>
              Save
            </Button>
            <Button type="button" variant="ghost" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
        </form>
      </article>
    );
  }

  return (
    <article className={styles.row}>
      <div className={styles.rowTop}>
        <span className={styles.title}>
          {accomplishment.title} · {accomplishment.points} pts
        </span>
        {isArchived && <span className={styles.archivedTag}>Archived</span>}
      </div>
      {accomplishment.description && <p className={styles.meta}>{accomplishment.description}</p>}
      <p className={styles.meta}>
        {DECORATION_KIND_LABELS[accomplishment.decoration]} ·{' '}
        {DECORATION_CATEGORIES.find((c) => c.value === accomplishment.category)?.label}
        {accomplishment.repeatable && ' · Repeatable'}
      </p>
      <Holders holders={accomplishment.holders ?? []} />
      {error && <p className={styles.error}>{error}</p>}
      {awarding && <AwardForm accomplishment={accomplishment} onDone={() => setAwarding(false)} />}
      <div className={styles.actions}>
        <Button type="button" variant="ghost" onClick={() => setEditing(true)}>
          Edit
        </Button>
        {!isArchived && !awarding && (
          <Button type="button" variant="ghost" onClick={() => setAwarding(true)}>
            Award
          </Button>
        )}
        <Button
          type="button"
          variant={isArchived ? 'default' : 'danger'}
          disabled={archiveMutation.isPending}
          onClick={() => archiveMutation.mutate(!isArchived)}
        >
          {isArchived ? 'Restore' : 'Archive'}
        </Button>
      </div>
    </article>
  );
}

/** Award the accomplishment to a crew member directly — no claim needed from them. */
function AwardForm({ accomplishment, onDone }: { accomplishment: Accomplishment; onDone: () => void }) {
  const queryClient = useQueryClient();
  const members = useQuery({ queryKey: ['members'], queryFn: getMembers });
  const me = useMe();
  const candidates = awardCandidates(members.data?.members ?? [], accomplishment, me.data?.id);
  const [userId, setUserId] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [awardedTo, setAwardedTo] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => awardAccomplishment(accomplishment.id, { userId, note: note.trim() || undefined }),
    onSuccess: () => {
      setAwardedTo(members.data?.members.find((m) => m.id === userId)?.displayName ?? 'them');
      setUserId('');
      setNote('');
      queryClient.invalidateQueries({ queryKey: ['accomplishments'] });
      queryClient.invalidateQueries({ queryKey: ['members'] });
      queryClient.invalidateQueries({ queryKey: ['claims'] });
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Something went wrong.'),
  });

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setAwardedTo(null);
    mutation.mutate();
  }

  return (
    <form className={styles.awardForm} onSubmit={handleSubmit}>
      <div className={styles.field}>
        <label htmlFor={`award-to-${accomplishment.id}`}>Award to</label>
        <select
          id={`award-to-${accomplishment.id}`}
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
          required
        >
          <option value="">Choose a crew member…</option>
          {candidates.map((m) => (
            <option key={m.id} value={m.id}>
              {m.displayName}
            </option>
          ))}
        </select>
        {members.data && candidates.length === 0 && <p className={styles.meta}>Everyone in the crew already holds this one.</p>}
      </div>
      <div className={styles.field}>
        <label htmlFor={`award-note-${accomplishment.id}`}>Note (optional)</label>
        <input
          id={`award-note-${accomplishment.id}`}
          value={note}
          maxLength={1000}
          placeholder="e.g. for leading the line at Bimini"
          onChange={(e) => setNote(e.target.value)}
        />
      </div>
      {accomplishment.repeatable && (
        <p className={styles.meta}>Repeatable: every award counts as one more repeat.</p>
      )}
      {error && <p className={styles.error}>{error}</p>}
      {awardedTo && <p className={styles.meta}>Awarded to {awardedTo}.</p>}
      <div className={styles.actions}>
        <Button type="submit" variant="primary" disabled={!userId || mutation.isPending}>
          Award
        </Button>
        <Button type="button" variant="ghost" onClick={onDone}>
          Done
        </Button>
      </div>
    </form>
  );
}

/** Who has earned the accomplishment (approved claims), with the approval date. */
function Holders({ holders }: { holders: AccomplishmentHolder[] }) {
  if (holders.length === 0) return <p className={styles.meta}>Not earned yet</p>;
  return (
    <div className={styles.holders}>
      <span className={styles.holdersLabel}>Earned by {holders.length}</span>
      <ul className={styles.holderList}>
        {holders.map((h, i) => (
          <li key={`${h.id}-${i}`} className={styles.holder} title={h.approvedAt ? `Approved ${formatDate(h.approvedAt)}` : undefined}>
            {h.avatarUrl ? (
              <img className={styles.avatar} src={h.avatarUrl} alt="" />
            ) : (
              <span className={styles.avatar} aria-hidden="true">
                {h.displayName.slice(0, 1)}
              </span>
            )}
            <span>{h.displayName}</span>
            {h.count > 1 && <span className={styles.holderDate}>×{h.count}</span>}
            {h.approvedAt && <span className={styles.holderDate}>{formatDate(h.approvedAt)}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

const EMPTY_DECORATION_VALUE: DecorationFieldsValue = {
  decoration: 'ribbon',
  category: 'service',
  repeatable: false,
  thresholds: ['', '', ''],
  art: null,
};

export function ManagePage() {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['accomplishments', { includeArchived: true }],
    queryFn: () => getAccomplishments({ includeArchived: true }),
  });

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [points, setPoints] = useState('0');
  const [decorationValue, setDecorationValue] = useState<DecorationFieldsValue>(EMPTY_DECORATION_VALUE);
  const [createError, setCreateError] = useState<string | null>(null);

  const createMutation = useMutation({
    mutationFn: () =>
      createAccomplishment({
        title,
        description,
        points: Number(points),
        decoration: decorationValue.decoration,
        category: decorationValue.category,
        repeatable: decorationValue.repeatable,
        starThresholds: decorationValue.repeatable ? inputsToThresholds(decorationValue.thresholds) : [],
        art: decorationValue.art,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accomplishments'] });
      setTitle('');
      setDescription('');
      setPoints('0');
      setDecorationValue(EMPTY_DECORATION_VALUE);
    },
    onError: (err) => setCreateError(err instanceof Error ? err.message : 'Something went wrong.'),
  });

  function handleCreate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setCreateError(null);
    createMutation.mutate();
  }

  const accomplishments = query.data?.accomplishments ?? [];

  return (
    <section>
      <h2 className={styles.heading}>Manage accomplishments</h2>
      <form className={styles.createForm} onSubmit={handleCreate}>
        <div className={styles.field}>
          <label htmlFor="new-title">Title</label>
          <input id="new-title" value={title} onChange={(e) => setTitle(e.target.value)} required />
        </div>
        <div className={styles.field}>
          <label htmlFor="new-description">Description</label>
          <textarea
            id="new-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
          />
        </div>
        <div className={styles.field}>
          <label htmlFor="new-points">Points</label>
          <input
            id="new-points"
            type="number"
            min={0}
            value={points}
            onChange={(e) => setPoints(e.target.value)}
          />
        </div>
        <DecorationFields idPrefix="new" value={decorationValue} onChange={setDecorationValue} />
        {createError && <p className={styles.error}>{createError}</p>}
        <div className={styles.actions}>
          <Button type="submit" variant="primary" disabled={createMutation.isPending}>
            Create
          </Button>
        </div>
      </form>

      {query.isLoading && <p>Loading…</p>}
      {query.isError && <p className={styles.error}>{(query.error as Error).message}</p>}
      <div className={styles.list}>
        {accomplishments.map((accomplishment) => (
          <ManageRow key={accomplishment.id} accomplishment={accomplishment} />
        ))}
      </div>
    </section>
  );
}
